/**
 * ContentCaptionSync — Dimus Video Components
 *
 * Componente de legenda kinetic dual-stack:
 * ┌────────────────────────────────────────────────────────────┐
 * │  STACK A: @remotion/captions (createTikTokStyleCaptions)   │
 * │  STACK B: CaptionCraft / SRT word-level timestamps         │
 * └────────────────────────────────────────────────────────────┘
 *
 * LOOK CANÔNICO DIMUS (CaptionCraft v1.1.0):
 * Camada 1: rolling highlight palavra-por-palavra (karaokê — BASE)
 * Camada 2: UMA palavra de carga semântica por beat com tratamento extra
 *
 * Regras de design (VETO do dono 2026-05-30):
 * - NUNCA retângulo/scrim/bloom atrás do texto
 * - Proteção via text-shadow dark + glow no destaque
 * - MÁX. 2 famílias tipográficas por peça (1 base + 1 destaque)
 * - Safe-zone: lados 7% / topo 12% / base 18%
 * - Posição variável mas organizada; default acima da cabeça ou centro
 *
 * Stack A: @remotion/captions output
 *   import { createTikTokStyleCaptions } from "@remotion/captions";
 *   const { captions } = createTikTokStyleCaptions({ transcript: words });
 *   → { captions: Array<{ text: string, startMs: number, endMs: number }> }
 *
 * Stack B: CaptionCraft SRT / word-level timestamps
 *   { word: string, start: number, end: number, emphasis?: boolean }[]
 *
 * ADR: ia-skills ADR-0001
 * Integração: skillcontentosdimus-captioncraft (timing/form spec)
 */
import React from "react";
import { interpolate, spring, useCurrentFrame, useVideoConfig } from "remotion";
import { Easing } from "remotion";

// ── Easings ──────────────────────────────────────────────────────────────────
const ease = {
  expoOut: Easing.bezier(0.19, 1, 0.22, 1),
  backOut: Easing.bezier(0.34, 1.56, 0.64, 1),
};

// ── Type: Stack A — @remotion/captions format ─────────────────────────────────
export interface RemotionCaption {
  /** Full text of the caption chunk */
  text: string;
  /** Start time in milliseconds */
  startMs: number;
  /** End time in milliseconds */
  endMs: number;
}

// ── Type: Stack B — CaptionCraft / SRT word-level ─────────────────────────────
export interface WordTimestamp {
  /** The word text */
  word: string;
  /** Start time in seconds (from produce_reel / Whisper SRT) */
  start: number;
  /** End time in seconds */
  end: number;
  /** Mark this word for emphasis (camada 2 — at most 1-2 per 30s clip) */
  emphasis?: boolean;
}

// ── Caption style presets (from CaptionCraft compositions.json) ───────────────
export type CaptionPreset =
  | "editorial-dimus"      // Cormorant italic + Coolvetica, verde #00DF82
  | "bold-center-pop"      // Coolvetica bold, karaokê 2026
  | "neon-glow-flicker"    // Humane condensed, glow animado
  | "humane-statement"     // Humane display, uma palavra por frame
  | "coolvetica-friendly"  // Coolvetica, sans default Dimus
  | "custom";              // usa as props custom abaixo

// ── Emphasis animation types ──────────────────────────────────────────────────
export type EmphasisAnimation = "scale-pop" | "color-flash" | "blur-reveal" | "none";

// ── Position presets ─────────────────────────────────────────────────────────
export type CaptionPosition =
  | "center"         // centro da safe zone (50% Y)
  | "above-subject"  // acima da cabeça, dentro da safe zone
  | "bottom-third"   // último terço da safe zone
  | "custom";        // usa top/left/right props

// ── Main Props ────────────────────────────────────────────────────────────────
export interface ContentCaptionSyncProps {
  // ── INPUT: escolha um dos dois formatos ────────────────────────────────────

  /**
   * Stack A: saída de createTikTokStyleCaptions().
   * Se fornecida, ignora `words`.
   */
  captions?: RemotionCaption[];

  /**
   * Stack B: array word-level com timestamps em segundos.
   * Usado quando vem do pipeline produce_reel → Whisper SRT.
   */
  words?: WordTimestamp[];

  // ── STYLING ────────────────────────────────────────────────────────────────

  /** Preset de estilo. Default: "bold-center-pop" */
  preset?: CaptionPreset;

  /** Cor da palavra ATIVA (highlight). Default: "#00DF82" (Dimus verde neon) */
  highlightColor?: string;

  /** Cor das palavras INATIVAS. Default: "#fff" com 55% opacity */
  baseColor?: string;

  /** Cor de ênfase extra (camada 2). Default: "#FECB18" (Dimus gold) */
  emphasisColor?: string;

  /** Fonte base. Default: "Coolvetica, system-ui, sans-serif" */
  fontFamily?: string;

  /** Fonte de destaque (emphasis). Default: "Humane, Impact, sans-serif" */
  emphasisFont?: string;

  /** Tamanho base em px. Default: 80 */
  fontSize?: number;

  /** Tamanho de ênfase em px. Default: 96 */
  emphasisFontSize?: number;

  /** Peso base. Default: 700 */
  fontWeight?: number;

  /** Tipo de animação na palavra de ênfase. Default: "scale-pop" */
  emphasisAnimation?: EmphasisAnimation;

  // ── LAYOUT ─────────────────────────────────────────────────────────────────

  /** Posição na tela. Default: "center" */
  position?: CaptionPosition;

  /** Offset Y customizado em px quando position="custom" */
  customTop?: number;

  /** Margem lateral em px (safe zone). Default: 80 */
  paddingHorizontal?: number;

  /** Max palavras por linha. Default: 4 */
  wordsPerLine?: number;

  /** Alinhamento. Default: "center" */
  textAlign?: "center" | "left" | "right";

  // ── TIMING ─────────────────────────────────────────────────────────────────

  /** FPS do projeto. Default: pega de useVideoConfig */
  fps?: number;

  /** Frame de início. Default: 0 */
  startFrame?: number;

  // ── SAFE ZONE ──────────────────────────────────────────────────────────────

  /** Ativa debug de safe zone (vermelho). Default: false */
  debugSafeZone?: boolean;
}

// ── Safe zone constants (IG Reel 1080×1920) ───────────────────────────────────
const SAFE = {
  top: 290,     // 15% → reservado para IG chrome
  bottom: 1500, // bottom 22% começa aqui
  left: 76,     // 7%
  right: 76,    // 7%
};

// ── Preset configs ───────────────────────────────────────────────────────────
interface PresetConfig {
  fontFamily: string;
  emphasisFont: string;
  fontSize: number;
  emphasisFontSize: number;
  fontWeight: number;
  highlightColor: string;
  emphasisColor: string;
  emphasisAnimation: EmphasisAnimation;
  textAlign: "center" | "left" | "right";
}

const PRESETS: Record<Exclude<CaptionPreset, "custom">, PresetConfig> = {
  "editorial-dimus": {
    fontFamily: "'Cormorant Garamond', 'Times New Roman', serif",
    emphasisFont: "'Coolvetica', system-ui, sans-serif",
    fontSize: 72,
    emphasisFontSize: 88,
    fontWeight: 600,
    highlightColor: "#00DF82",
    emphasisColor: "#FECB18",
    emphasisAnimation: "blur-reveal",
    textAlign: "left",
  },
  "bold-center-pop": {
    fontFamily: "'Coolvetica', 'Helvetica Neue', system-ui, sans-serif",
    emphasisFont: "'Humane', 'Impact', system-ui, sans-serif",
    fontSize: 80,
    emphasisFontSize: 96,
    fontWeight: 700,
    highlightColor: "#00DF82",
    emphasisColor: "#FECB18",
    emphasisAnimation: "scale-pop",
    textAlign: "center",
  },
  "neon-glow-flicker": {
    fontFamily: "'Humane', 'Impact', system-ui, sans-serif",
    emphasisFont: "'Humane', 'Impact', system-ui, sans-serif",
    fontSize: 88,
    emphasisFontSize: 104,
    fontWeight: 900,
    highlightColor: "#00DF82",
    emphasisColor: "#34E2FF",
    emphasisAnimation: "color-flash",
    textAlign: "center",
  },
  "humane-statement": {
    fontFamily: "'Humane', 'Impact', system-ui, sans-serif",
    emphasisFont: "'Humane', 'Impact', system-ui, sans-serif",
    fontSize: 96,
    emphasisFontSize: 112,
    fontWeight: 900,
    highlightColor: "#fff",
    emphasisColor: "#FECB18",
    emphasisAnimation: "scale-pop",
    textAlign: "center",
  },
  "coolvetica-friendly": {
    fontFamily: "'Coolvetica', system-ui, sans-serif",
    emphasisFont: "'Coolvetica', system-ui, sans-serif",
    fontSize: 76,
    emphasisFontSize: 90,
    fontWeight: 700,
    highlightColor: "#00DF82",
    emphasisColor: "#FECB18",
    emphasisAnimation: "scale-pop",
    textAlign: "center",
  },
};

// ── Stack A adapter: RemotionCaption[] → WordTimestamp[] ─────────────────────
function remotionCaptionsToWords(captions: RemotionCaption[]): WordTimestamp[] {
  const words: WordTimestamp[] = [];
  for (const cap of captions) {
    const wordList = cap.text.trim().split(/\s+/);
    const durationPerWord = (cap.endMs - cap.startMs) / wordList.length / 1000;
    wordList.forEach((w, i) => {
      const start = cap.startMs / 1000 + i * durationPerWord;
      words.push({
        word: w,
        start,
        end: start + durationPerWord,
      });
    });
  }
  return words;
}

// ── Word highlight animation ──────────────────────────────────────────────────
const AnimatedWord: React.FC<{
  word: string;
  isActive: boolean;
  isEmphasis: boolean;
  baseColor: string;
  highlightColor: string;
  emphasisColor: string;
  fontFamily: string;
  emphasisFont: string;
  fontSize: number;
  emphasisFontSize: number;
  fontWeight: number;
  emphasisAnimation: EmphasisAnimation;
  frame: number;
  activeFrame: number;
}> = ({
  word,
  isActive,
  isEmphasis,
  baseColor,
  highlightColor,
  emphasisColor,
  fontFamily,
  emphasisFont,
  fontSize,
  emphasisFontSize,
  fontWeight,
  emphasisAnimation,
  frame,
  activeFrame,
}) => {
  const localSinceActive = frame - activeFrame;

  // ── Camada 1: rolling highlight (karaokê base) ────────────────────────────
  const color = isActive ? (isEmphasis ? emphasisColor : highlightColor) : baseColor;
  const currentFont = isEmphasis && isActive ? emphasisFont : fontFamily;
  const currentSize = isEmphasis && isActive ? emphasisFontSize : fontSize;

  // ── Camada 2: emphasis animation ──────────────────────────────────────────
  let emphasisScale = 1;
  let emphasisBlur = 0;
  let emphasisGlow = "none";

  if (isActive && isEmphasis && emphasisAnimation !== "none") {
    switch (emphasisAnimation) {
      case "scale-pop": {
        const sp = spring({
          frame: Math.max(0, localSinceActive),
          fps: 30,
          config: { damping: 9, stiffness: 160 },
          from: 0.8,
          to: 1,
        });
        emphasisScale = sp;
        emphasisGlow = `drop-shadow(0 0 12px ${emphasisColor})`;
        break;
      }
      case "blur-reveal": {
        emphasisBlur = interpolate(localSinceActive, [0, 12], [10, 0], {
          extrapolateLeft: "clamp",
          extrapolateRight: "clamp",
          easing: ease.expoOut,
        });
        break;
      }
      case "color-flash": {
        // Color already set; add glow flicker
        const flicker = 0.7 + Math.sin(localSinceActive * 0.6) * 0.3;
        emphasisGlow = `drop-shadow(0 0 ${12 * flicker}px ${emphasisColor})`;
        break;
      }
      default:
        break;
    }
  }

  return (
    <span
      style={{
        display: "inline-block",
        fontFamily: currentFont,
        fontSize: currentSize,
        fontWeight,
        color,
        transform: `scale(${emphasisScale})`,
        filter: emphasisBlur > 0 ? `blur(${emphasisBlur}px)` : emphasisGlow,
        // text-shadow: proteção sem scrim (VETO: nunca retângulo/plate)
        textShadow: isActive
          ? `0 2px 5px rgba(0,0,0,.9), 0 0 ${isEmphasis ? "18px" : "6px"} ${color}66`
          : "0 2px 5px rgba(0,0,0,.9)",
        whiteSpace: "pre",
        willChange: "transform, filter, color",
        transition: isActive ? undefined : "color 0.1s ease",
        letterSpacing: "-0.02em",
      }}
    >
      {word}{" "}
    </span>
  );
};

// ── Main Component ────────────────────────────────────────────────────────────
export const ContentCaptionSync: React.FC<ContentCaptionSyncProps> = ({
  captions,
  words: wordsProp,
  preset = "bold-center-pop",
  highlightColor,
  baseColor = "rgba(255,255,255,0.55)",
  emphasisColor,
  fontFamily,
  emphasisFont,
  fontSize,
  emphasisFontSize,
  fontWeight,
  emphasisAnimation,
  position = "center",
  customTop,
  paddingHorizontal = 80,
  wordsPerLine = 4,
  textAlign = "center",
  startFrame = 0,
  debugSafeZone = false,
}) => {
  const frame = useCurrentFrame();
  const { fps: videoFps } = useVideoConfig();

  // ── Resolve word list (Stack A or B) ────────────────────────────────────────
  const words: WordTimestamp[] = captions
    ? remotionCaptionsToWords(captions)
    : wordsProp ?? [];

  // ── Resolve preset ────────────────────────────────────────────────────────
  const resolvedPreset = preset !== "custom" ? PRESETS[preset] : null;

  const resolvedFontFamily  = fontFamily  ?? resolvedPreset?.fontFamily  ?? "'Coolvetica', system-ui, sans-serif";
  const resolvedEmphFont    = emphasisFont ?? resolvedPreset?.emphasisFont ?? resolvedFontFamily;
  const resolvedFontSize    = fontSize     ?? resolvedPreset?.fontSize     ?? 80;
  const resolvedEmphSize    = emphasisFontSize ?? resolvedPreset?.emphasisFontSize ?? resolvedFontSize + 16;
  const resolvedWeight      = fontWeight   ?? resolvedPreset?.fontWeight   ?? 700;
  const resolvedHlColor     = highlightColor  ?? resolvedPreset?.highlightColor  ?? "#00DF82";
  const resolvedEmphColor   = emphasisColor   ?? resolvedPreset?.emphasisColor   ?? "#FECB18";
  const resolvedEmphAnim    = emphasisAnimation ?? resolvedPreset?.emphasisAnimation ?? "scale-pop";
  const resolvedTextAlign   = resolvedPreset?.textAlign ?? textAlign;

  // ── Current time in seconds ───────────────────────────────────────────────
  const currentSec = (frame - startFrame) / videoFps;

  // ── Find active word ──────────────────────────────────────────────────────
  // A word is "active" (highlighted) from its start to end time
  const activeIdx = words.findIndex(
    (w) => currentSec >= w.start && currentSec < w.end
  );

  // ── Find current chunk (wordsPerLine words around active) ─────────────────
  // Show the line containing the active word
  const lineIdx = activeIdx >= 0 ? Math.floor(activeIdx / wordsPerLine) : 0;
  const chunkStart = lineIdx * wordsPerLine;
  const chunkEnd = Math.min(chunkStart + wordsPerLine, words.length);
  const chunk = words.slice(chunkStart, chunkEnd);

  // ── Position calculation ──────────────────────────────────────────────────
  let topPx: number;
  switch (position) {
    case "center":
      topPx = Math.floor((SAFE.top + SAFE.bottom) / 2) - resolvedFontSize;
      break;
    case "above-subject":
      // Upper 40% of safe zone
      topPx = SAFE.top + Math.floor((SAFE.bottom - SAFE.top) * 0.15);
      break;
    case "bottom-third":
      // Bottom 30% of safe zone
      topPx = SAFE.top + Math.floor((SAFE.bottom - SAFE.top) * 0.68);
      break;
    case "custom":
      topPx = customTop ?? SAFE.top + Math.floor((SAFE.bottom - SAFE.top) * 0.5);
      break;
    default:
      topPx = Math.floor((SAFE.top + SAFE.bottom) / 2) - resolvedFontSize;
  }

  // ── Chunk entrance animation ──────────────────────────────────────────────
  const chunkEntryFrame = chunkStart < words.length
    ? Math.floor(words[chunkStart].start * videoFps) + startFrame
    : 0;
  const chunkLocal = Math.max(0, frame - chunkEntryFrame);
  const chunkOp = interpolate(chunkLocal, [0, 8], [0, 1], {
    extrapolateLeft: "clamp",
    extrapolateRight: "clamp",
    easing: ease.expoOut,
  });
  const chunkY = interpolate(chunkLocal, [0, 12], [12, 0], {
    extrapolateLeft: "clamp",
    extrapolateRight: "clamp",
    easing: ease.expoOut,
  });

  if (words.length === 0) return null;

  return (
    <>
      {/* Debug safe zone overlay */}
      {debugSafeZone && (
        <div
          style={{
            position: "absolute",
            top: SAFE.top,
            left: SAFE.left,
            right: SAFE.right,
            bottom: 1920 - SAFE.bottom,
            border: "2px dashed rgba(255,0,0,0.4)",
            pointerEvents: "none",
          }}
        />
      )}

      {/* Caption block */}
      <div
        style={{
          position: "absolute",
          top: topPx,
          left: paddingHorizontal,
          right: paddingHorizontal,
          display: "flex",
          flexWrap: "wrap",
          justifyContent: resolvedTextAlign === "center" ? "center" : resolvedTextAlign === "right" ? "flex-end" : "flex-start",
          textAlign: resolvedTextAlign,
          opacity: chunkOp,
          transform: `translateY(${chunkY}px)`,
          willChange: "transform, opacity",
          lineHeight: 1.1,
        }}
      >
        {chunk.map((w, i) => {
          const wordIdx = chunkStart + i;
          const isActive = wordIdx === activeIdx;
          const isEmphasis = Boolean(w.emphasis) && isActive;
          const activeFrame = isActive
            ? Math.floor(w.start * videoFps) + startFrame
            : 0;

          return (
            <AnimatedWord
              key={`${wordIdx}-${w.word}`}
              word={w.word}
              isActive={isActive}
              isEmphasis={isEmphasis}
              baseColor={baseColor}
              highlightColor={resolvedHlColor}
              emphasisColor={resolvedEmphColor}
              fontFamily={resolvedFontFamily}
              emphasisFont={resolvedEmphFont}
              fontSize={resolvedFontSize}
              emphasisFontSize={resolvedEmphSize}
              fontWeight={resolvedWeight}
              emphasisAnimation={resolvedEmphAnim}
              frame={frame}
              activeFrame={activeFrame}
            />
          );
        })}
      </div>
    </>
  );
};

// ── Usage Examples ────────────────────────────────────────────────────────────
/*
// ────────────────────────────────────────────────────────────
// STACK A — @remotion/captions
// ────────────────────────────────────────────────────────────
// No pipeline (produce_reel ou server-side):
//   import { createTikTokStyleCaptions } from "@remotion/captions";
//   const { captions } = createTikTokStyleCaptions({ transcript: whisperWords });
//
// No TSX:
<ContentCaptionSync
  captions={captions}          // saída do createTikTokStyleCaptions
  preset="bold-center-pop"
  position="center"
  startFrame={BEAT.hook}
/>

// ────────────────────────────────────────────────────────────
// STACK B — CaptionCraft / word-level timestamps
// ────────────────────────────────────────────────────────────
// Vem do peca.json (campo `words`) gerado pelo produce_reel via Whisper
const words: WordTimestamp[] = [
  { word: "seu",     start: 0.00, end: 0.18 },
  { word: "preço",   start: 0.19, end: 0.45, emphasis: true },  // camada 2
  { word: "mínimo",  start: 0.46, end: 0.80 },
  { word: "não",     start: 0.82, end: 0.95 },
  { word: "é",       start: 0.96, end: 1.05 },
  { word: "o",       start: 1.06, end: 1.14 },
  { word: "que",     start: 1.15, end: 1.25 },
  { word: "você",    start: 1.26, end: 1.45 },
  { word: "acha",    start: 1.46, end: 1.72 },
];

<ContentCaptionSync
  words={words}
  preset="bold-center-pop"
  position="above-subject"
  highlightColor="#00DF82"
  emphasisColor="#FECB18"
  emphasisAnimation="scale-pop"
  wordsPerLine={4}
  startFrame={0}
/>

// ────────────────────────────────────────────────────────────
// Debug de safe zone (apenas em desenvolvimento):
// ────────────────────────────────────────────────────────────
<ContentCaptionSync words={words} preset="bold-center-pop" debugSafeZone />
*/

;
    var _remotion_globalVariableA;
    var _remotion_globalVariableB;
    // Legacy CSS implementations will `eval` browser code in a Node.js context
    // to extract CSS. For backwards compatibility, we need to check we're in a
    // browser context before continuing.
    if (typeof self !== 'undefined' &&
        // AMP / No-JS mode does not inject these helpers:
        '$RefreshHelpers$' in self) {
        const currentExports = __webpack_module__.exports;
        const prevExports = (_remotion_globalVariableB = (_remotion_globalVariableA = __webpack_module__.hot.data) === null || _remotion_globalVariableA === void 0 ? void 0 : _remotion_globalVariableA.prevExports) !== null && _remotion_globalVariableB !== void 0 ? _remotion_globalVariableB : null;
        // This cannot happen in MainTemplate because the exports mismatch between
        // templating and execution.
        self.$RefreshHelpers$.registerExportsForReactRefresh(currentExports, __webpack_module__.id);
        // A module can be accepted automatically based on its exports, e.g. when
        // it is a Refresh Boundary.
        if (self.$RefreshHelpers$.isReactRefreshBoundary(currentExports)) {
            // Save the previous exports on update so we can compare the boundary
            // signatures.
            __webpack_module__.hot.dispose((data) => {
                data.prevExports = currentExports;
            });
            // Unconditionally accept an update to this module, we'll check if it's
            // still a Refresh Boundary later.
            __webpack_module__.hot.accept();
            // This field is set when the previous version of this module was a
            // Refresh Boundary, letting us know we need to check for invalidation or
            // enqueue an update.
            if (prevExports !== null) {
                // A boundary can become ineligible if its exports are incompatible
                // with the previous exports.
                //
                // For example, if you add/remove/change exports, we'll want to
                // re-execute the importing modules, and force those components to
                // re-render. Similarly, if you convert a class component to a
                // function, we want to invalidate the boundary.
                if (self.$RefreshHelpers$.shouldInvalidateReactRefreshBoundary(prevExports, currentExports)) {
                    __webpack_module__.hot.invalidate();
                }
                else {
                    self.$RefreshHelpers$.scheduleUpdate();
                }
            }
        }
        else {
            // Since we just executed the code for the module, it's possible that the
            // new exports made it ineligible for being a boundary.
            // We only care about the case when we were _previously_ a boundary,
            // because we already accepted this update (accidental side effect).
            const isNoLongerABoundary = prevExports !== null;
            if (isNoLongerABoundary) {
                __webpack_module__.hot.invalidate();
            }
        }
    }
